import React, { useState, useEffect } from "react";
import {
  Card,
  Tabs,
  Form,
  Input,
  Select,
  Radio,
  Checkbox,
  Button,
  Upload,
  Table,
  Tag,
  Steps,
  Progress,
  Avatar,
  Space,
  Divider,
  Alert,
  Row,
  Typography,
  Badge,
  message,
  Modal,
} from "antd";
import {
  UserOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  UploadOutlined,
  EditOutlined,
  EyeOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import axios, { all } from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

import { v4 as uuidv4 } from "uuid";
import firebaseConfig from "../firebaseConfig";
import { initializeApp } from "firebase/app";
import { firebaseAPI } from "../firebaseConfig";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  egyptCities,
  genderOptions,
  gradYearOptions,
  majorOptions,
  universityOptions,
} from "../data/profileOptions";
import axiosInstance from "../utils/axiosInstance";
import DeadlinePassedMessage from "../components/DeadlinePassesMessage";

const app = initializeApp(firebaseConfig);
const storage = getStorage(app);

const uploadFileToFirebase = async (file) => {
  try {
    // Generate a unique identifier and prepend it to the file name
    const uid = uuidv4();
    const uniqueFileName = `${uid}-${file.name}`;
    const storageRef = ref(storage, `ur-uploads/${uniqueFileName}`);

    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error; // Rethrow the error to be handled by the caller
  }
};

const deleteFileFromFirebase = async (fileUrl) => {
  const fileRef = ref(storage, fileUrl);
  await deleteObject(fileRef);
};

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Option } = Select;
const { Step } = Steps;

const statusDisplayMapper = {
  pending: "Under Review",
  awaiting_submission: "Awaiting Submission",
  qualified: "Qualified",
  disqualified: "Disqualified",
};

const ParticipantProfilePage = ({ signUpAllowed }) => {
  //-------------- Form & Navigation Hooks --------------
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [programName, setProgramName] = useState(
    decodeURIComponent(location.pathname.split("/")[1] || "")
  );
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);

  //-------------- Local State --------------
  const [profileExists, setProfileExists] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [programRounds, setProgramRounds] = useState([]);
  const [participantRounds, setParticipantRounds] = useState([]);
  const [currentRoundStatus, setCurrentRoundStatus] = useState("");
  const [prevCurrentRoundStatus, setPrevCurrentRoundStatus] = useState("");
  // additional flags and data
  const [editOrUpdateProfile, setEditOrUpdateProfile] = useState(false);
  const [checkProfileStatus, setCheckProfileStatus] = useState(false);
  const [tableData, setTableData] = useState([]);

  // Calculate progress
  const totalRounds = programRounds.length;
  const completedRounds = Math.max(
    0,
    participantRounds.length - (currentRoundStatus === "qualified" ? 0 : 1)
  );
  const progressPercent = (completedRounds / participantRounds.length) * 100;
  const nextRound =
    participantRounds[participantRounds.length - 1] || programRounds[0];
  const statusColumns = [
    {
      title: "Round",
      dataIndex: "roundName",
      key: "roundName",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const config = {
          qualified: { color: "success", text: "Qualified" },
          pending: { color: "processing", text: "Under Review" },
          awaiting_submission: {
            color: "processing",
            text: "Awaiting Submission",
          },
          disqualified: { color: "error", text: "Not Qualified" },
        };
        const { color, text } = config[status] || {
          color: "default",
          text: status,
        };
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const statusData = participantRounds.map((round, index) => {
    const isLast = index === participantRounds.length - 1;
    const roundStatus = isLast ? currentRoundStatus : "qualified";

    return {
      key: index,
      roundName: round,
      status: roundStatus,
    };
  });
  //-------------- Lifecycle Effects --------------
  useEffect(() => {
    setProgramName(decodeURIComponent(location.pathname.split("/")[1] || ""));
    fetchProfileData();
    fetchRounds();
  }, [location.pathname]);

  //-------------- Data Fetchers --------------
  const fetchProfileData = async () => {
    try {
      const token = Cookies.get("token") || "";
      const response = await axiosInstance.get(
        `${firebaseAPI}/participantProfile`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const profileData = Object.values(response.data)[0];

      if (profileData) {
        // mark existence and hydrate form
        setProfileExists(true);
        const cvUrl = profileData.cvUrl || null;
        const initialValues = {
          mobileNumber: profileData.mobileNumber || "",
          city: profileData.city || "",
          university: profileData.university || "",
          major: profileData.major || "",
          gradYear: profileData.gradYear || "",
          gender: profileData.gender || "",
          hasDisability:
            profileData.hasDisability !== undefined
              ? profileData.hasDisability
              : null,
          disabilitySpecification: profileData.disabilitySpecification || "",
          correctInfoConfirmation:
            profileData.acknowledgements?.correctInfo || false,
          acknowledgeInfo:
            profileData.acknowledgements?.acknowledgeDisqualification || false,
          acknowledgeAttendance:
            profileData.acknowledgements?.acknowledgeOnSiteAttendance || false,
          cvFiles: cvUrl
            ? [
                {
                  uid: cvUrl,
                  name: extractFileName(cvUrl),
                  status: "done",
                  url: cvUrl,
                },
              ]
            : [],
        };
        form.setFieldsValue(initialValues);
        setProfileExists(true);
        setUserData(initialValues);

        // participant rounds
        const program = profileData.participantPrograms?.find(
          (p) => p.programName === programName
        );
        if (program) {
          setParticipantRounds(program.participantRounds || []);
          setCurrentRoundStatus(program.status || "Not Started");
        }
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setFormKey((k) => k + 1);
    }
  };

  const fetchRounds = async () => {
    try {
      const token = Cookies.get("token") || "";
      const { data } = await axiosInstance.get(
        `${firebaseAPI}/program/${programName}/rounds`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProgramRounds(data.programRounds || []);
    } catch (error) {
      console.error("Error fetching program rounds:", error);
    } finally {
      setFormKey((k) => k + 1);
    }
  };

  //-------------- Validation Helpers --------------
  const validateMobileNumber = (_, value) => {
    if (!value || /^\d{11}$/.test(value)) return Promise.resolve();
    return Promise.reject(new Error("Please enter a correct mobile number"));
  };

  const extractFileName = (url) => {
    try {
      const decoded = decodeURIComponent(new URL(url).pathname);
      return decoded.split("/").pop();
    } catch {
      return url;
    }
  };

  const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

  const handleCVUpload = (file) => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const isPDFOrWord = validTypes.includes(file.type);
    const isSizeValid = file.size / 1024 / 1024 < 5;

    if (!isPDFOrWord || !isSizeValid) {
      message.error("Please upload a PDF or Word document under 5 MB.");
      return Upload.LIST_IGNORE;
    }

    const preview = URL.createObjectURL(file);
    form.setFieldsValue({
      cvFiles: [
        {
          uid: file.uid,
          name: file.name,
          status: "done",
          originFileObj: file,
          url: preview,
        },
      ],
    });
    return false;
  };

  //-------------- Form Submission --------------
  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue();
      const cvList = values.cvFiles || [];
      const fileItem = cvList[0];
      const newFile = fileItem?.originFileObj;
      const existingUrl = fileItem?.url;

      let uploadedCvUrl = "";
      if (newFile) {
        uploadedCvUrl = await uploadFileToFirebase(newFile);
        if (existingUrl && existingUrl !== uploadedCvUrl) {
          await deleteFileFromFirebase(existingUrl);
        }
      } else {
        uploadedCvUrl = existingUrl || "";
      }
      const payload = {
        programName,
        mobileNumber: values.mobileNumber,
        city: values.city,
        university: values.university,
        major: values.major,
        gradYear: values.gradYear,
        gender: values.gender,
        cvUrl: uploadedCvUrl,
        hasDisability: values.hasDisability,
        disabilitySpecification: values.disabilitySpecification,
        acknowledgements: {
          correctInfo: values.correctInfoConfirmation,
          acknowledgeDisqualification: values.acknowledgeInfo,
          acknowledgeOnSiteAttendance: values.acknowledgeAttendance,
        },
      };

      const token = Cookies.get("token") || "";
      await axiosInstance.post(`${firebaseAPI}/participantProfile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      message.success("Profile information saved successfully");
      await fetchProfileData();
      setIsEditing(false);
      return true;
    } catch (error) {
      console.error("Error saving profile:", error);
      message.error(
        "Failed to save/update profile information. Please try again."
      );
      return false;
    }
  };

  const isFormFilled = () => {
    const base = [
      "mobileNumber",
      "city",
      "university",
      "major",
      "gradYear",
      "hasDisability",
      "correctInfoConfirmation",
      "acknowledgeInfo",
      "acknowledgeAttendance",
      "gender",
    ];
    const needSpec = form.getFieldValue("hasDisability") === true;
    const required = needSpec
      ? [...base, "disabilitySpecification", "cvFiles"]
      : [...base, "cvFiles"];
    const vals = form.getFieldsValue(required);
    const errs = form.getFieldsError(required);
    const exist = required.every(
      (k) => vals[k] !== undefined && vals[k] !== null
    );
    const noErr = errs.every((f) => f.errors.length === 0);
    return exist && noErr;
  };

  const handleShowParticipantProfilePage = () => {
    setEditOrUpdateProfile(true);
    setCheckProfileStatus(false);
  };

  const handleShowCheckProfileStatus = () => {
    setEditOrUpdateProfile(false);
    setCheckProfileStatus(true);
  };

  const handleProceed = () => {
    const nextUrl = participantRounds
      ? `/${programName}/participantform/${
          participantRounds[participantRounds.length - 1]
        }`
      : programRounds
      ? `/${programName}/participantform/${programRounds[0]}`
      : `/${programName}/participantform/Application Round`;
    setIsModalVisible(false);
    // Add your logic to navigate to assessment
    ////console.log(nextUrl)
    window.location.href = nextUrl;
  };

  // Check Status Preparations
  // Creating the table data with statuses
  useEffect(() => {
    if (participantRounds) {
      const newTableData = participantRounds.map((round, index) => {
        const isLast = index === participantRounds.length - 1;
        const roundStatus = isLast ? currentRoundStatus : "qualified";

        return {
          key: index,
          roundName: round,
          status: roundStatus,
        };
      });
      setTableData(newTableData);
    }
  }, [participantRounds, currentRoundStatus]);

  // Columns configuration for the Ant Design Table component
  const columns = [
    {
      title: "Round Name",
      dataIndex: "roundName",
      key: "roundName",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => {
        let color;
        switch (text) {
          case "qualified":
            color = "green";
            break;
          case "disqualified":
            color = "red";
            break;
          case "pending":
            color = "yellow";
            break;
          default:
            color = "default";
        }
        return <Tag color={color}>{text.toUpperCase()}</Tag>;
      },
    },
  ];
  const [userData, setUserData] = useState({
    mobileNumber: "",
    university: "",
    gender: "",
    major: "",
    city: "",
    hasDisability: null,
    disabilitySpecification: "",
    gradYear: "",
    cvFile: null,
    cvFileName: "",
    cvUrl: null,
  });

  // Warnings

  const [isModalVisible, setIsModalVisible] = useState(false);

  const showConfirmation = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px" }}>
      {/* Header Section */}
      <Card
        hoverable
        style={{
          marginBottom: 24,
          background:
            "linear-gradient(135deg,rgb(23, 79, 151) 0%,rgba(14, 62, 221, 0.77) 100%)",
          color: "white",
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          border: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              size={64}
              icon={<UserOutlined />}
              style={{ marginRight: 16 }}
            />
            <div>
              <Title level={2} style={{ color: "white", margin: 0 }}>
                Track Your Summer Academy Application Progress
              </Title>
              <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 16 }}>
                Start with your profile, then complete each round to move
                forward.
              </Text>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Progress
              type="circle"
              percent={progressPercent}
              format={(percent) =>
                `${completedRounds}/${participantRounds.length}`
              }
              style={{ marginBottom: 8 }}
            />
            <Text style={{ color: "white", display: "block" }}>
              Rounds Completed
            </Text>
          </div>
        </div>
      </Card>

      {/* Progress Steps */}
      <Card style={{ marginBottom: 24 }}>
        <Steps
          current={participantRounds.length - 1}
          status={currentRoundStatus === "disqualified" ? "error" : "process"}
        >
          {participantRounds.map((round, index) => (
            <Step
              key={round}
              title={round}
              description={
                index < participantRounds.length - 1
                  ? "Completed"
                  : index === participantRounds.length - 1
                  ? statusDisplayMapper[currentRoundStatus]
                  : "Pending"
              }
              icon={
                index < participantRounds.length - 1 ? (
                  <CheckCircleOutlined />
                ) : index === participantRounds.length - 1 &&
                  currentRoundStatus === "pending" ? (
                  <ClockCircleOutlined />
                ) : undefined
              }
            />
          ))}
        </Steps>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          size="large"
        >
          <TabPane
            tab={
              <span>
                <UserOutlined />
                Profile
                {profileExists && (
                  <Badge status="success" style={{ marginLeft: 8 }} />
                )}
              </span>
            }
            key="profile"
          >
            <div style={{ maxWidth: 600, margin: "0 auto" }}>
              {!profileExists && !signUpAllowed && <DeadlinePassedMessage />}
              {profileExists && (
                <>
                  {/* Profile Header */}
                  <div style={{ textAlign: "center", marginBottom: 32 }}>
                    <Title level={3}>
                      {profileExists ? "Your Profile" : "Create Profile"}
                    </Title>
                    <Text type="secondary">
                      {profileExists
                        ? "Review and update your information"
                        : "Complete your participant profile to continue"}
                    </Text>

                    {profileExists && !isEditing && (
                      <div style={{ marginTop: 16 }}>
                        <Button
                          type="primary"
                          icon={<EditOutlined />}
                          onClick={() => setIsEditing(true)}
                          disabled={participantRounds.length > 1}
                        >
                          Edit Profile
                        </Button>
                        {participantRounds.length > 1 && (
                          <Text
                            type="secondary"
                            style={{ display: "block", marginTop: 8 }}
                          >
                            Profile cannot be edited after multiple rounds
                          </Text>
                        )}
                      </div>
                    )}
                  </div>

                  {!profileExists || isEditing ? (
                    <Form
                      form={form}
                      key={formKey}
                      layout="vertical"
                      initialValues={userData}
                      onValuesChange={(changed, all) => setUserData(all)}
                      onFinish={handleSubmit}
                      style={{ textAlign: "left" }}
                    >
                      <Form.Item
                        label="Mobile Number"
                        name="mobileNumber"
                        rules={[
                          {
                            required: true,
                            message: "Please enter your mobile number",
                          },
                          { validator: validateMobileNumber },
                        ]}
                        tooltip="Enter in international format, e.g.,01234567890"
                      >
                        <Input
                          allowClear
                          size="large"
                          placeholder="e.g.01234567890"
                        />
                      </Form.Item>

                      <Form.Item
                        label="City"
                        name="city"
                        rules={[
                          {
                            required: true,
                            message: "Please select your city",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select your city"
                          size="large"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {egyptCities.map((ct) => (
                            <Option key={ct} value={ct}>
                              {ct}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="University"
                        name="university"
                        rules={[
                          {
                            required: true,
                            message: "Please select your university",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select a university"
                          size="large"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {universityOptions.map((uni) => (
                            <Option key={uni} value={uni}>
                              {uni}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Major"
                        name="major"
                        rules={[
                          {
                            required: true,
                            message: "Please select your major",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          allowClear
                          placeholder="Select your major"
                          size="large"
                          filterOption={(input, option) =>
                            option.children
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {majorOptions.map((m) => (
                            <Option key={m} value={m}>
                              {m}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Graduation Year"
                        name="gradYear"
                        rules={[
                          {
                            required: true,
                            message: "Please select your graduation year",
                          },
                        ]}
                      >
                        <Select
                          allowClear
                          placeholder="Select your graduation year"
                          size="large"
                        >
                          {gradYearOptions.map((yr) => (
                            <Option key={yr} value={yr}>
                              {yr}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        label="Gender"
                        name="gender"
                        rules={[
                          {
                            required: true,
                            message: "Please select your gender",
                          },
                        ]}
                      >
                        <Select
                          allowClear
                          placeholder="Select Gender"
                          size="large"
                        >
                          {genderOptions.map((opt) => (
                            <Option key={opt.value} value={opt.value}>
                              {opt.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        label="CV"
                        name="cvFiles"
                        valuePropName="fileList"
                        getValueFromEvent={normFile}
                        rules={[
                          { required: true, message: "Please upload your CV" },
                        ]}
                      >
                        <Dragger
                          beforeUpload={handleCVUpload}
                          multiple={false}
                          showUploadList
                        >
                          <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                        </Dragger>
                      </Form.Item>

                      <Form.Item
                        label="Disability"
                        name="hasDisability"
                        rules={[
                          {
                            required: true,
                            message: "Please select an option",
                          },
                        ]}
                      >
                        <Radio.Group>
                          <Radio value={true}>Yes</Radio>
                          <Radio value={false}>No</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {userData.hasDisability && (
                        <Form.Item
                          label="Disability Specification"
                          name="disabilitySpecification"
                          rules={[
                            {
                              required: true,
                              message:
                                "Please provide details about your disability",
                            },
                          ]}
                        >
                          <Input.TextArea
                            autoSize={{ minRows: 3 }}
                            placeholder="Provide details..."
                          />
                        </Form.Item>
                      )}

                      {/* <Alert type="info" showIcon message="Disclaimer" description="Mandatory availability onsite for the full program month." style={{ marginBottom: 20 }} /> */}

                      <Form.Item
                        name="correctInfoConfirmation"
                        valuePropName="checked"
                        rules={[
                          {
                            validator: (_, val) =>
                              val
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      "You must confirm that all information is correct"
                                    )
                                  ),
                          },
                        ]}
                      >
                        <Checkbox>
                          <Text strong>Disclaimer:</Text> Submitting more than
                          one application will result in
                          <Text type="danger">
                            {" "}
                            immediate disqualification{" "}
                          </Text>
                          from the current process and any future opportunities
                          with Dell Technologies.
                          <br />
                          <Text>Please apply only once.</Text>
                        </Checkbox>
                      </Form.Item>

                      <Form.Item
                        name="acknowledgeInfo"
                        valuePropName="checked"
                        rules={[
                          {
                            validator: (_, val) =>
                              val
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      "Please confirm that your information is accurate and complete."
                                    )
                                  ),
                          },
                        ]}
                      >
                        <Checkbox>
                          I confirm that all the information I have entered is
                          <Text strong> accurate and complete</Text>. Any false
                          information may lead to
                          <Text type="danger"> disqualification</Text> from
                          future opportunities with Dell Technologies.
                        </Checkbox>
                      </Form.Item>

                      <Form.Item
                        name="acknowledgeAttendance"
                        valuePropName="checked"
                        rules={[
                          {
                            validator: (_, val) =>
                              val
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      "You must acknowledge the on-site attendance requirement"
                                    )
                                  ),
                          },
                        ]}
                      >
                        <Checkbox>
                          I acknowledge that
                          <Text type="danger">
                            {" "}
                            on-site attendance for the full duration
                          </Text>{" "}
                          of the program month is mandatory.
                        </Checkbox>
                      </Form.Item>

                      <Divider />

                      <Form.Item>
                        <Row justify="center">
                          <Space direction="vertical" size="large">
                            <Button
                              type="primary"
                              htmlType="submit"
                              size="large"
                              disabled={!isFormFilled()}
                            >
                              {profileExists
                                ? "Update Profile"
                                : "Save Profile"}
                            </Button>
                            <Link to="#" onClick={() => navigate("/")}>
                              <Button
                                type="primary"
                                ghost
                                size="large"
                                disabled={!isFormFilled()}
                              >
                                Proceed to{" "}
                                {participantRounds?.length
                                  ? participantRounds.slice(-1)[0]
                                  : programRounds[0] || "Application Round"}
                              </Button>
                            </Link>
                          </Space>
                        </Row>
                      </Form.Item>
                    </Form>
                  ) : (
                    <div>
                      {/* Profile Summary View */}
                      <Card
                        title="Profile Summary"
                        style={{ marginBottom: 16 }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 16,
                          }}
                        >
                          <div>
                            <Text type="secondary">Mobile:</Text>
                            <div>{userData?.mobileNumber}</div>
                          </div>
                          <div>
                            <Text type="secondary">City:</Text>
                            <div>{userData?.city}</div>
                          </div>
                          <div>
                            <Text type="secondary">University:</Text>
                            <div>{userData?.university}</div>
                          </div>
                          <div>
                            <Text type="secondary">Major:</Text>
                            <div>{userData?.major}</div>
                          </div>
                          <div>
                            <Text type="secondary">Graduation Year:</Text>
                            <div>{userData?.gradYear}</div>
                          </div>
                          <div>
                            <Text type="secondary">Gender:</Text>
                            <div style={{ textTransform: "capitalize" }}>
                              {userData?.gender}
                            </div>
                          </div>
                        </div>

                        {userData?.cvFiles?.length > 0 && (
                          <div style={{ marginTop: 16 }}>
                            <Text type="secondary">CV:</Text>
                            <div>
                              <FileTextOutlined style={{ marginRight: 8 }} />
                              {userData?.cvFiles[0].name}
                            </div>
                          </div>
                        )}
                      </Card>
                    </div>
                  )}
                </>
              )}
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <EyeOutlined />
                Status
              </span>
            }
            key="status"
          >
            <div style={{ maxWidth: 800, margin: "0 auto" }}>
              <Title
                level={3}
                style={{ textAlign: "center", marginBottom: 32 }}
              >
                Application Status
              </Title>

              <Table
                columns={statusColumns}
                dataSource={statusData}
                pagination={false}
                style={{ marginBottom: 32 }}
              />

              {currentRoundStatus === "pending" && (
                <Alert
                  message="Application In Progress"
                  description="Your application is currently being reviewed. You will be notified of the results soon."
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {currentRoundStatus === "qualified" && nextRound && (
                <Alert
                  message="Congratulations!"
                  description={`You have qualified for the next round: ${nextRound}`}
                  type="success"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              {currentRoundStatus === "disqualified" && (
                <Alert
                  message="Application Update"
                  description="Unfortunately, you have not qualified for the next round. Thank you for your interest."
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}
            </div>
          </TabPane>

          {profileExists &&
            nextRound != "Online Interview" &&
            currentRoundStatus == "awaiting_submission" && (
              <TabPane
                tab={
                  <span>
                    <ArrowRightOutlined />
                    Next Round
                  </span>
                }
                key="next"
              >
                <div>
                  <div
                    style={{
                      maxWidth: 600,
                      margin: "0 auto",
                      textAlign: "center",
                    }}
                  >
                    <Title level={3}>Ready for Next Round</Title>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 32,
                      }}
                    >
                      You're qualified to proceed to the {nextRound}
                    </Text>

                    <Card style={{ marginBottom: 32 }}>
                      <Title level={4}>{nextRound}</Title>
                      <Text>
                        Click the button below to begin your{" "}
                        {nextRound.toLowerCase()}. Make sure you have enough
                        time to complete it as progress may not be saved.
                      </Text>
                    </Card>

                    <Button
                      type="primary"
                      size="large"
                      icon={<ArrowRightOutlined />}
                      onClick={showConfirmation}
                      disabled={currentRoundStatus === "pending"}
                    >
                      {currentRoundStatus === "pending"
                        ? "Current Round In Progress"
                        : `Proceed to ${nextRound}`}
                    </Button>
                  </div>
                  <Modal
                    title={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <ExclamationCircleOutlined
                          style={{ color: "#faad14", marginRight: 8 }}
                        />
                        Assessment Confirmation
                      </div>
                    }
                    open={isModalVisible}
                    onOk={handleProceed}
                    onCancel={handleCancel}
                    okText="Yes, I'm Ready"
                    cancelText="Cancel"
                    width={500}
                    centered
                  >
                    <div style={{ padding: "20px 0" }}>
                      <Text
                        strong
                        style={{
                          fontSize: 16,
                          display: "block",
                          marginBottom: 16,
                        }}
                      >
                        Are you sure you want to enter the assessment?
                      </Text>

                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: "100%" }}
                      >
                        {nextRound === "Quiz Round" && (
                          <>
                            {" "}
                            <div
                              style={{
                                padding: 16,
                                backgroundColor: "#fff7e6",
                                border: "1px solid #ffd591",
                                borderRadius: 6,
                              }}
                            >
                              <Text strong style={{ color: "#d46b08" }}>
                                ⚠️ Important Requirements:
                              </Text>
                              <ul
                                style={{
                                  marginTop: 8,
                                  marginBottom: 0,
                                  paddingLeft: 20,
                                }}
                              >
                                <li>
                                  Do not open any other tabs during the
                                  assessment
                                </li>
                                <li>Do not switch between applications</li>
                                <li>
                                  Ensure you have adequate time to complete the
                                  assessment
                                </li>
                              </ul>
                            </div>
                            <div
                              style={{
                                padding: 16,
                                backgroundColor: "#fff1f0",
                                border: "1px solid #ffccc7",
                                borderRadius: 6,
                              }}
                            >
                              <Text strong style={{ color: "#cf1322" }}>
                                🚫 Warning:
                              </Text>
                              <Text style={{ display: "block", marginTop: 4 }}>
                                Any irregular behavior or tab switching will
                                result in disqualification from this and future
                                Dell Technologies opportunities.
                              </Text>
                            </div>
                            <div
                              style={{
                                padding: 16,
                                backgroundColor: "#f6ffed",
                                border: "1px solid #b7eb8f",
                                borderRadius: 6,
                              }}
                            >
                              <Text strong style={{ color: "#389e0d" }}>
                                📝 Assessment Overview:
                              </Text>
                              <Text style={{ display: "block", marginTop: 4 }}>
                                This assessment includes multiple-choice
                                questions (6–7 per category) covering:
                              </Text>
                              <ul
                                style={{
                                  marginTop: 8,
                                  marginBottom: 8,
                                  paddingLeft: 20,
                                }}
                              >
                                <li>Dell Portfolio Awareness</li>
                                <li>General Technical Knowledge</li>
                                <li>CS Fundamentals</li>
                                <li>IQ & Logical Reasoning</li>
                                <li>Work Ethics</li>
                                <li>Dell Culture</li>
                                <li>AI & Machine Learning</li>
                              </ul>
                              <Text>
                                You'll have <strong>30 minutes</strong> to
                                complete <strong>45 questions</strong>. Just do
                                your best—no pressure!
                              </Text>
                            </div>
                          </>
                        )}
                        {nextRound === "Video Interview" && (
                          <div
                            style={{
                              padding: 16,
                              backgroundColor: "#e6f7ff",
                              border: "1px solid #91d5ff",
                              borderRadius: 6,
                            }}
                          >
                            <Text strong style={{ color: "#096dd9" }}>
                              🎥 Video Interview Instructions:
                            </Text>
                            <Text style={{ display: "block", marginTop: 8 }}>
                              Please record a <strong>2-3 mintes</strong> video
                              answering the following questions:
                            </Text>
                            <ul
                              style={{
                                marginTop: 8,
                                marginBottom: 8,
                                paddingLeft: 20,
                              }}
                            >
                              <li>Introduce yourself briefly</li>
                              <li>
                                Why do you think you are the ideal candidate for
                                the summer academy?
                              </li>
                              <li>
                                Name one accomplishment you are proud of the
                                most.
                              </li>
                            </ul>
                            <Text strong>Please note:</Text>
                            <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                              <li>
                                All questions must be answered in English.
                              </li>
                              <li>
                                Pausing is not allowed on the video (Prepare
                                your answers before starting to record).
                              </li>
                            </ul>
                          </div>
                        )}
                      </Space>
                    </div>
                  </Modal>
                </div>
              </TabPane>
            )}
          {profileExists &&
            nextRound == "Online Interview" &&
            currentRoundStatus == "awaiting_submission" && (
              <TabPane
                tab={
                  <span>
                    <ArrowRightOutlined />
                    Next Round
                  </span>
                }
                key="next"
              >
                <div>
                  <div
                    style={{
                      maxWidth: 600,
                      margin: "0 auto",
                      textAlign: "center",
                    }}
                  >
                    <Title level={3}>Ready for Next Round</Title>
                    <Text
                      type="secondary"
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 32,
                      }}
                    >
                      You're qualified to proceed to the {nextRound}
                    </Text>

                    <Card style={{ marginBottom: 32 }}>
                      <Title level={4}>{nextRound}</Title>
                      <Text>
                        We will be reaching out to you soon with the details of
                        your {nextRound}.
                      </Text>
                    </Card>
                  </div>
                </div>
              </TabPane>
            )}
        </Tabs>
      </Card>
    </div>
  );
};

export default ParticipantProfilePage;
